import React from 'react';
import PropTypes from 'prop-types';
import { Gap } from '@reservamos/elements';
import FilterChip from '../../../ui/atoms/FilterChip';
import { filtersTypes } from '../../../constants/filtersTypes';

/**
 * Component for rendering an individual recline option.
 * @component
 * @param {Object} props - The component props.
 * @param {string} props.title - The title of the recline option.
 * @param {string} props.description - The description of the recline option.
 * @param {string} props.icon - The icon of the recline option.
 * @param {string} props.iconColor - The color of the icon.
 * @param {string} props.value - The value of the recline option.
 * @param {boolean} props.isActive - Indicates whether the recline option is active or not.
 * @param {boolean} props.isAvailable - Indicates whether the recline option is available or not.
 * @param {Function} props.onSelect - The function to be called when the option is selected.
 * @param {string} props.i18Key - The translation key for the label.
 * @returns {JSX.Element} The rendered component.
 */
const ReclineOption = ({
  title,
  description,
  icon,
  iconColor,
  value,
  isActive,
  onSelect,
  isAvailable,
  i18Key,
}) => {
  return (
    <FilterChip
      i18Key={i18Key}
      text={title}
      secondText={description}
      icon={icon}
      iconColor={iconColor}
      filterType={filtersTypes.RECLINE_OPTIONS}
      value={value}
      onSelect={onSelect}
      active={isActive}
      notAvailable={!isAvailable}
    />
  );
};

ReclineOption.propTypes = {
  title: PropTypes.string,
  description: PropTypes.string,
  icon: PropTypes.string,
  iconColor: PropTypes.string,
  value: PropTypes.object,
  isActive: PropTypes.bool,
  isAvailable: PropTypes.bool,
  onSelect: PropTypes.func,
  i18Key: PropTypes.string,
};

/**
 * Component for rendering a list of recline options.
 * @component
 * @param {Object} props - The component props.
 * @param {Array} props.options - The array of recline options to be rendered.
 * @param {Array} props.selectedReclineOptions - The array of selected recline options.
 * @param {Function} props.onSelectOption - The function to be called when an option is selected.
 * @returns {JSX.Element} The rendered component.
 */
const ReclineOptionList = ({ options, selectedReclineOptions, onSelectOption }) => {
  if (!options || !options.length) return null;

  /**
   * Checks if a recline option is currently active/selected.
   * @param {Object} option - The option to check, must have a value property.
   * @param {Array|null} selectedOptions - Array of selected options (primitives or objects), can be null/undefined.
   * @returns {boolean} True if the option is active/selected, false otherwise.
   */
  const checkIfActive = (option, selectedOptions) => {
    if (typeof option.value === 'object') {
      return selectedOptions?.some((item) => {
        return Object.keys(item).every((key) => item[key] === option.value[key]);
      });
    }
    return selectedOptions?.includes(option.value);
  };

  return (
    <Gap columnGap="M" rowGap="S">
      {options.map((option) => (
        <ReclineOption
          key={option.id}
          id={option.id}
          i18Key={option.label}
          title={option.title}
          description={option.description}
          icon={option.icon}
          iconColor={option.iconColor}
          value={option.value}
          isActive={checkIfActive(option, selectedReclineOptions)}
          onSelect={onSelectOption}
          isAvailable={option.hasAvailability}
        />
      ))}
    </Gap>
  );
};

ReclineOptionList.propTypes = {
  options: PropTypes.array,
  selectedReclineOptions: PropTypes.array,
  onSelectOption: PropTypes.func,
};

export default ReclineOptionList;
